<template>
  <v-dialog
    v-model="dialog"
    width="800"
    :fullscreen="$vuetify.display.smAndDown"
  >
    <v-card flat class="movement-dialog-card">
      <v-toolbar color="red-darken-3" density="comfortable">
        <v-toolbar-title class="text-truncate text-body-1 text-sm-h6">
          {{ $t("parts.movement_history.label") }}
        </v-toolbar-title>
        <v-spacer />
        <span v-if="count != null" class="text-subtitle-2 mr-2">
          {{ count }}
        </span>
        <v-btn
          variant="text"
          icon
          color="white"
          aria-label="Close"
          @click="dialog = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- Mobile card list -->
      <div class="d-md-none movement-history-mobile-list px-3 pt-2 pb-2">
        <v-progress-linear
          v-if="loading"
          indeterminate
          color="red"
          class="mb-2"
        />

        <div
          v-for="(item, index) in paginatedItems"
          :key="item._id || `${item.date}-${index}`"
          class="movement-history-mobile-card"
          :class="{
            'movement-history-mobile-card--selected': index === activeRow,
          }"
          @click="activeRow = activeRow === index ? null : index"
        >
          <v-btn
            v-if="item.date"
            color="black"
            variant="outlined"
            size="small"
            class="movement-history-mobile-card__date mb-2"
            readonly
            @click.stop
          >
            {{ formatMoment(item.date) }}
          </v-btn>

          <v-chip
            v-if="getStatusName(item.status)"
            :color="getStatusColor(item.status)"
            size="small"
            variant="flat"
            class="movement-history-mobile-card__status"
          >
            {{ movementLabel(item) }}
          </v-chip>

          <div
            v-if="item.user"
            class="text-caption text-grey-darken-1 mt-1"
          >
            <span class="font-weight-medium text-grey-darken-2">
              {{ $t("parts.movement_history.table.user") }}:
            </span>
            {{ item.user.name }} {{ item.user.surname }}
          </div>
          <div
            v-if="item.client"
            class="text-caption text-grey-darken-1 mt-1"
          >
            <span class="font-weight-medium text-grey-darken-2">
              {{ $t("parts.movement_history.table.client") }}:
            </span>
            {{ item.client.name }}
          </div>
        </div>

        <v-alert
          v-if="!loading && items.length < 1"
          type="info"
          variant="tonal"
          class="mt-2"
        >
          No data available.
        </v-alert>
      </div>

      <div class="movement-history-table-host">
        <v-data-table
          v-model:options="pagination"
          :headers="headers"
          :items="items"
          :loading="loading"
          :items-per-page-options="[5, 10, 25, 50]"
          items-per-page-text="Rows per page"
          class="elevation-1"
        >
          <template #loading>
            <v-progress-linear color="red" indeterminate />
          </template>

          <template #item="{ item, index }">
            <tr
              :key="item._id"
              :class="{ tableRowColorSelected: index === activeRow }"
              class="tableRowColor"
              @click="activeRow = activeRow === index ? null : index"
            >
              <td class="text-xs">
                <v-chip :color="getStatusColor(item.status)">
                  {{ movementLabel(item) }}
                </v-chip>
              </td>

              <td class="text-xs">
                <span v-if="item.user">
                  {{ item.user.name }} {{ item.user.surname }}
                </span>
              </td>

              <td class="text-xs">
                <span v-if="item.client">
                  {{ item.client.name }}
                </span>
              </td>

              <td class="text-xs">
                <v-btn color="black" variant="outlined" size="small">
                  {{ formatMoment(item.date) }}
                </v-btn>
              </td>
            </tr>
          </template>
        </v-data-table>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import getRequest from "../api/getRequest";

export default {
  data: () => ({
    dialog: false,
    loading: false,
    items: [],
    count: null,
    headers: [
      {
        title: "Status",
        sortable: false,
        key: "status",
      },
      {
        title: "User",
        sortable: false,
        key: "user.name",
      },
      {
        title: "Client",
        sortable: false,
        key: "client.name",
      },
      {
        title: "Date",
        sortable: true,
        key: "date",
      },
    ],
    activeRow: null,
    pagination: {
      itemsPerPage: 5,
      page: 1,
      sortBy: [{ key: "date", order: "desc" }],
    },
  }),
  computed: {
    paginatedItems() {
      const items = [...this.items];
      const sortBy = this.pagination.sortBy?.[0];
      if (sortBy?.key === "date") {
        items.sort((a, b) => {
          const da = new Date(a.date).getTime();
          const db = new Date(b.date).getTime();
          return sortBy.order === "desc" ? db - da : da - db;
        });
      }
      const page = this.pagination.page ?? 1;
      const perPage = this.pagination.itemsPerPage ?? 5;
      const start = (page - 1) * perPage;
      return items.slice(start, start + perPage);
    },
  },
  watch: {
    dialog(value) {
      if (!value) {
        this.items = [];
        this.activeRow = null;
      }
    },
    "$i18n.locale"() {
      this.getLocale();
    },
    pagination: {
      handler() {
        this.activeRow = null;
      },
      deep: true,
    },
  },
  mounted() {
    this.getLocale();
    this.getStatusName(status);
  },
  methods: {
    formatMoment(date) {
      moment.locale(localStorage.getItem("language"));
      return moment(date).format("LLL");
    },
    getStatusName(status) {
      switch (status) {
        case 0.1:
          return this.$t("parts.movement_history.status.inserted_empty_parts");
        case 0.2:
          return this.$t("parts.movement_history.status.deleted_empty_parts");
        case 1.0:
          return this.$t(
            "parts.movement_history.status.inserted_temporary_parts",
          );
        case 1.5:
          return this.$t(
            "parts.movement_history.status.deleted_temporary_parts",
          );
        case 1.1:
          return this.$t("parts.movement_history.status.sent_for_approval");
        case 1.2:
          return this.$t("parts.movement_history.status.sent_for_review");
        case 1.3:
          return this.$t("parts.movement_history.status.refused");
        case 1.4:
          return this.$t("parts.movement_history.status.approved");
        case 1.6:
          return this.$t("parts.movement_history.status.saved_approve_parts");
        case 2.0:
          return this.$t("parts.movement_history.status.inserted_inventory");
        case 2.05:
          return this.$t("parts.movement_history.status.changed_qr_id");
        case 2.1:
          return this.$t(
            "parts.movement_history.status.deleted_from_inventory",
          );
        case 3.0:
          return this.$t("parts.movement_history.status.returned_sales");
        case 3.1:
          return this.$t("parts.movement_history.status.returned_shop");
        case 4.0:
          return this.$t("parts.movement_history.status.re_entered");
        case 5.0:
          return this.$t("parts.movement_history.status.found");
        case 6.0:
          return this.$t("parts.movement_history.status.sold");
        case 6.1:
          return this.$t("parts.movement_history.status.sold_online");
        case 6.2:
          return this.$t("parts.movement_history.status.sold_ebay");
        case 6.3:
          return this.$t("parts.movement_history.status.sold_ovoko");
        case 6.4:
          return this.$t("parts.movement_history.status.sold_ebay_leko");
        case 7.0:
          return this.$t("parts.movement_history.status.lost");
        case 8.0:
          return this.$t("parts.movement_history.status.damaged");
        case 8.1:
          return this.$t("parts.movement_history.status.damaged_sales");
        case 8.2:
          return this.$t("parts.movement_history.status.damaged_shop");
        case 8.3:
          return this.$t("parts.movement_history.status.damaged_lost");
        default:
          return "";
      }
    },
    getStatusColor(status) {
      switch (true) {
        case [0.1, 1.0, 2.0, 4.0].includes(status):
          return "success";
        case [2.05].includes(status):
          return "purple";
        case [1.1, 1.2].includes(status):
          return "info";
        case [1.3].includes(status):
          return "error";
        case [1.4, 1.6].includes(status):
          return "success";
        case [1.5, 2.1].includes(status):
          return "warning";
        case [3.0, 3.1].includes(status):
          return "grey-darken-3";
        case [5.0].includes(status):
          return "info";
        case [6.0, 6.1, 6.2, 6.3, 6.4].includes(status):
          return "green-darken-2";
        case [7.0].includes(status):
          return "grey-darken-2";
        case [8.0, 8.1, 8.2, 8.3].includes(status):
          return "red-darken-1";
        default:
          return "grey";
      }
    },

    movementLabel(item) {
      const name = this.getStatusName(item && item.status);
      if (Number(item && item.status) !== 2.05 || !item.movement_data) return name;
      const from = item.movement_data.source || "";
      const to = item.movement_data.destination || "";
      if (!from && !to) return name;
      return `${name} (${from} → ${to})`;
    },
    getLocale() {
      this.headers[0].title = this.$t("parts.movement_history.table.status");
      this.headers[1].title = this.$t("parts.movement_history.table.user");
      this.headers[2].title = this.$t("parts.movement_history.table.client");
      this.headers[3].title = this.$t("parts.movement_history.table.date");
    },
    open(part) {
      this.dialog = true;
      this.loading = true;
      this.activeRow = null;
      this.pagination.page = 1;
      const query = [];
      if (part.old_id) query.push(`old_id=${part.old_id}`);
      if (part.backup_id) query.push(`backup_id=${part.backup_id}`);
      getRequest(
        `/history/part/single/${part._id}`,
        query.join("&"),
      )
        .then((response) => {
          this.loading = false;
          const data = response.history ? response.history : response;

          if (data) {
            this.items = data.sort(
              (a, b) => new Date(b.date) - new Date(a.date),
            );
          } else {
            this.items = response.history ? response.history : response;
          }
          this.count = response.count;
        })
        .catch(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.movement-dialog-card {
  display: flex;
  flex-direction: column;
  max-height: 100%;
}

@media (max-width: 959.98px) {
  .movement-history-table-host :deep(.v-table__wrapper),
  .movement-history-table-host :deep(.v-data-table__wrapper),
  .movement-history-table-host :deep(.v-data-table__wrapper__scroll) {
    display: none !important;
  }
  .movement-history-table-host :deep(.v-data-table-footer) {
    padding-inline: 12px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }
  .movement-history-mobile-list {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
  }
}

@media (min-width: 960px) {
  .movement-history-mobile-list {
    display: none !important;
  }
}

.movement-history-mobile-card {
  position: relative;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 10px;
  padding: 10px 10px 40px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}
.movement-history-mobile-card--selected {
  outline: 2px solid rgb(var(--v-theme-primary));
}
.movement-history-mobile-card__date {
  height: auto !important;
  min-height: 0 !important;
  font-size: 0.75rem !important;
  text-transform: none;
  letter-spacing: normal;
  max-width: 100%;
}
.movement-history-mobile-card__status {
  position: absolute;
  bottom: 8px;
  right: 8px;
  max-width: calc(100% - 16px);
  height: auto !important;
  white-space: normal;
  text-align: right;
}
</style>
